import { authHeaders } from './credentials.js';
import { RedesignError, request } from './errors.js';
import { applyBindings, type ComfyWorkflow, type WorkflowBindings } from './workflow.js';
import type {
  RedesignInput,
  RedesignResult,
  RedesignRunOptions,
  RedesignTarget,
} from './types.js';

export interface ComfyConfig {
  /** Base URL of the ComfyUI server, without a trailing path. */
  baseUrl: string;
  apiKey?: string;
  /** Managed hosts differ; self-hosted ComfyUI needs no auth at all. */
  authHeader?: string;
  authPrefix?: string;
  /** Workflow in API format. */
  workflow: ComfyWorkflow;
  bindings: WorkflowBindings;
  pollIntervalMs?: number;
  timeoutMs?: number;
  /** Randomise the seed on every run unless the caller supplies one. */
  randomiseSeed?: boolean;
}

interface UploadedImage {
  name: string;
  subfolder?: string;
  type?: string;
}

interface HistoryOutputs {
  outputs?: Record<string, { images?: Array<{ filename: string; subfolder?: string; type?: string }> }>;
  status?: { completed?: boolean; status_str?: string; messages?: unknown[] };
}

/**
 * Runs a workflow on a ComfyUI server (self-hosted or managed).
 *
 * The protocol is the stable one every ComfyUI host exposes:
 *
 *   POST /upload/image     multipart, returns the stored filename
 *   POST /prompt           the patched workflow, returns a prompt_id
 *   GET  /history/{id}     polled until outputs appear
 *   GET  /view?filename=   the produced images
 *
 * Progress comes from the WebSocket at /ws when it connects, and falls back to
 * polling - managed hosts often terminate at a proxy that does not forward
 * WebSockets, and that must not be fatal.
 */
export class ComfyTarget implements RedesignTarget {
  readonly id = 'comfyui';
  readonly label = 'ComfyUI workflow';

  constructor(private readonly config: ComfyConfig) {}

  get endpoint(): string {
    return this.base;
  }

  private get base(): string {
    return this.config.baseUrl.replace(/\/+$/, '');
  }

  private get headers(): Record<string, string> {
    return authHeaders(this.config.apiKey, this.config.authHeader, this.config.authPrefix);
  }

  async run(input: RedesignInput, options: RedesignRunOptions = {}): Promise<RedesignResult> {
    const { onProgress, signal } = options;
    const clientId = randomId();

    onProgress?.({ stage: 'uploading', fraction: null, message: 'uploading the cut-out' });
    const image = await this.#upload(input.image, 'cutout.png', signal);
    const mask =
      input.mask && this.config.bindings.maskNode
        ? await this.#upload(input.mask, 'cutout-mask.png', signal)
        : undefined;

    const seed =
      input.seed ?? (this.config.randomiseSeed === false ? undefined : Math.floor(Math.random() * 2 ** 31));

    const workflow = applyBindings(this.config.workflow, this.config.bindings, {
      image: referenceOf(image),
      ...(mask ? { mask: referenceOf(mask) } : {}),
      ...(input.prompt !== undefined ? { prompt: input.prompt } : {}),
      ...(input.negativePrompt !== undefined ? { negativePrompt: input.negativePrompt } : {}),
      ...(seed !== undefined ? { seed } : {}),
    });

    onProgress?.({ stage: 'queued', fraction: null, message: 'queued' });
    const queued = (await (
      await request(
        `${this.base}/prompt`,
        {
          method: 'POST',
          headers: { 'content-type': 'application/json', ...this.headers },
          body: JSON.stringify({ prompt: workflow, client_id: clientId }),
          signal,
        },
        'Queueing the workflow',
      )
    ).json()) as { prompt_id?: string; error?: unknown; node_errors?: Record<string, unknown> };

    if (!queued.prompt_id) {
      throw new RedesignError(
        `ComfyUI rejected the workflow: ${JSON.stringify(queued.error ?? queued.node_errors ?? queued)}`,
        'Usually a missing model or a node input the workflow does not actually have. Run it once in the ComfyUI UI to confirm it works there.',
      );
    }

    const socket = this.#openProgressSocket(clientId, onProgress);
    try {
      const history = await this.#waitForHistory(queued.prompt_id, signal, onProgress);
      onProgress?.({ stage: 'downloading', fraction: null, message: 'fetching results' });
      const images = await this.#collect(history, signal);
      if (images.length === 0) {
        throw new RedesignError(
          'The workflow finished without producing an image.',
          'The graph probably has no SaveImage / PreviewImage node, which is what puts images in the history.',
        );
      }
      return { images, info: { promptId: queued.prompt_id, seed } };
    } finally {
      socket?.close();
    }
  }

  async #upload(blob: Blob, filename: string, signal?: AbortSignal): Promise<UploadedImage> {
    const body = new FormData();
    body.append('image', blob, filename);
    body.append('overwrite', 'true');
    body.append('type', 'input');

    const response = await request(
      `${this.base}/upload/image`,
      { method: 'POST', headers: this.headers, body, signal },
      'Uploading the image',
    );
    const uploaded = (await response.json()) as UploadedImage;
    if (!uploaded?.name) throw new RedesignError('Upload returned no filename.');
    return uploaded;
  }

  async #waitForHistory(
    promptId: string,
    signal: AbortSignal | undefined,
    onProgress: RedesignRunOptions['onProgress'],
  ): Promise<HistoryOutputs> {
    const interval = this.config.pollIntervalMs ?? 1000;
    const deadline = Date.now() + (this.config.timeoutMs ?? 10 * 60_000);

    for (;;) {
      if (signal?.aborted) throw new DOMException('redesign aborted', 'AbortError');
      if (Date.now() > deadline) {
        throw new RedesignError(
          'Timed out waiting for ComfyUI.',
          'The job may still be queued behind others. Raise the timeout, or check the queue on the server.',
        );
      }

      const response = await request(
        `${this.base}/history/${promptId}`,
        { headers: this.headers, signal },
        'Polling history',
      );
      const history = (await response.json()) as Record<string, HistoryOutputs>;
      const entry = history[promptId];
      if (entry?.outputs && Object.keys(entry.outputs).length > 0) return entry;

      // `status_str` turns to "error" without outputs when a node throws.
      if (entry?.status?.status_str === 'error') {
        throw new RedesignError(
          'The workflow failed on the server.',
          'Open the ComfyUI queue/history for the full node error.',
        );
      }

      onProgress?.({ stage: 'running', fraction: null, message: 'generating' });
      await sleep(interval, signal);
    }
  }

  async #collect(history: HistoryOutputs, signal?: AbortSignal): Promise<Blob[]> {
    const images: Blob[] = [];
    for (const output of Object.values(history.outputs ?? {})) {
      for (const image of output.images ?? []) {
        const query = new URLSearchParams({
          filename: image.filename,
          subfolder: image.subfolder ?? '',
          type: image.type ?? 'output',
        });
        const response = await request(
          `${this.base}/view?${query.toString()}`,
          { headers: this.headers, signal },
          'Downloading a result',
        );
        images.push(await response.blob());
      }
    }
    return images;
  }

  /** Best-effort progress. A host that does not proxy WebSockets just gets polling. */
  #openProgressSocket(
    clientId: string,
    onProgress: RedesignRunOptions['onProgress'],
  ): WebSocket | null {
    if (typeof WebSocket === 'undefined' || !onProgress) return null;
    try {
      const url = `${this.base.replace(/^http/, 'ws')}/ws?clientId=${clientId}`;
      const socket = new WebSocket(url);
      socket.addEventListener('message', (event) => {
        if (typeof event.data !== 'string') return;
        try {
          const message = JSON.parse(event.data) as {
            type?: string;
            data?: { value?: number; max?: number; node?: string };
          };
          if (message.type === 'progress' && message.data?.max) {
            onProgress({
              stage: 'running',
              fraction: (message.data.value ?? 0) / message.data.max,
              message: 'generating',
            });
          }
        } catch {
          // Non-JSON frames (binary previews) are not interesting here.
        }
      });
      socket.addEventListener('error', () => socket.close());
      return socket;
    } catch {
      return null;
    }
  }
}

/** ComfyUI refers to uploads as `name [type]` or `subfolder/name`. */
function referenceOf(image: UploadedImage): string {
  return image.subfolder ? `${image.subfolder}/${image.name}` : image.name;
}

function randomId(): string {
  return `design-ai-${Math.random().toString(36).slice(2)}`;
}

function sleep(ms: number, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    const timer = setTimeout(resolve, ms);
    signal?.addEventListener(
      'abort',
      () => {
        clearTimeout(timer);
        reject(new DOMException('redesign aborted', 'AbortError'));
      },
      { once: true },
    );
  });
}
